/**
 * Pure helpers for Our Journey Section slider and progress bar.
 */

export const CONTENT_FADE_OUT_MS = 250;
export const MOBILE_MAX_WIDTH = 767;

/**
 * Whether the current viewport uses the stacked mobile layout.
 *
 * @return {boolean} True when viewport is mobile width.
 */
export function isMobileLayout() {
	return window.matchMedia( `(max-width: ${ MOBILE_MAX_WIDTH }px)` ).matches;
}

/**
 * Clamp a number between min and max.
 *
 * @param {number} value Value to clamp.
 * @param {number} min   Minimum.
 * @param {number} max   Maximum.
 * @return {number} Clamped value.
 */
export function clampIndex( value, min, max ) {
	return Math.min( Math.max( value, min ), max );
}

/**
 * Normalize a Splide destination value to a logical slide index.
 *
 * @param {number} destIndex Destination index from Splide events.
 * @param {number} length    Number of real slides.
 * @return {number} Logical slide index.
 */
export function normalizeSlideIndex( destIndex, length ) {
	if ( length <= 0 ) {
		return 0;
	}

	return ( ( destIndex % length ) + length ) % length;
}

/**
 * Resolve the active logical slide index for content/progress sync.
 *
 * @param {{ index: number, length: number, Components?: { Slides?: { get: Function } } }} splide      Splide instance.
 * @param {number}                                                                         [destIndex] Splide move/moved dest argument.
 * @return {number} Logical slide index.
 */
export function resolveSlideIndex( splide, destIndex ) {
	const slideCount = splide.length || 0;

	if ( slideCount <= 0 ) {
		return 0;
	}

	if ( typeof destIndex === 'number' && ! Number.isNaN( destIndex ) ) {
		return normalizeSlideIndex( destIndex, slideCount );
	}

	const activeSlide = splide.Components?.Slides?.get()?.find( ( slide ) =>
		slide.slide.classList.contains( 'is-active' )
	);

	if ( activeSlide ) {
		return activeSlide.index;
	}

	return normalizeSlideIndex( splide.index, slideCount );
}

/**
 * Get the horizontal center of a progress dot relative to the track.
 *
 * @param {HTMLElement} point     Progress point button.
 * @param {DOMRect}     trackRect Track bounding rect.
 * @return {number} Dot center offset.
 */
export function getDotCenter( point, trackRect ) {
	const dot = point.querySelector(
		'.alteia-our-journey-section__progress-dot'
	);

	if ( ! dot ) {
		return 0;
	}

	const dotRect = dot.getBoundingClientRect();

	return dotRect.left - trackRect.left + dotRect.width / 2;
}

/**
 * Update the progress bar line, fill, and dot states.
 *
 * @param {number}      activeIndex Active slide index.
 * @param {HTMLElement} section     Section root element.
 */
export function updateProgressBar( activeIndex, section ) {
	const progressTrack = section.querySelector(
		'.alteia-our-journey-section__progress-track'
	);
	const progressPoints = section.querySelectorAll( '[data-journey-year]' );

	if ( ! progressTrack || ! progressPoints.length ) {
		return;
	}

	const safeIndex = clampIndex( activeIndex, 0, progressPoints.length - 1 );

	const progressLine = section.querySelector(
		'.alteia-our-journey-section__progress-line'
	);
	const progressFill = section.querySelector(
		'[data-journey-progress-fill]'
	);

	const trackRect = progressTrack.getBoundingClientRect();
	const firstCenter = getDotCenter( progressPoints[ 0 ], trackRect );
	const lastCenter = getDotCenter(
		progressPoints[ progressPoints.length - 1 ],
		trackRect
	);
	const activeCenter = getDotCenter( progressPoints[ safeIndex ], trackRect );

	// In RTL the first year sits on the right, so firstCenter > lastCenter.
	// Use min/max so the track and fill still span correctly.
	const lineLeft = Math.min( firstCenter, lastCenter );
	const lineWidth = Math.abs( lastCenter - firstCenter );
	const fillLeft = Math.min( firstCenter, activeCenter );
	const fillWidth = Math.abs( activeCenter - firstCenter );

	if ( progressLine ) {
		progressLine.style.left = `${ lineLeft }px`;
		progressLine.style.width = `${ lineWidth }px`;
	}

	if ( progressFill ) {
		progressFill.style.left = `${ fillLeft }px`;
		progressFill.style.width = `${ fillWidth }px`;
	}

	progressPoints.forEach( ( point, index ) => {
		const isActive = index === safeIndex;
		const isPassed = index <= safeIndex;

		point.classList.toggle(
			'alteia-our-journey-section__progress-point--active',
			isActive
		);
		point.classList.toggle(
			'alteia-our-journey-section__progress-point--passed',
			isPassed
		);
		point.setAttribute( 'aria-current', isActive ? 'true' : 'false' );
	} );
}

/**
 * Schedule progress bar update after layout is ready.
 *
 * @param {number}      activeIndex Active slide index.
 * @param {HTMLElement} section     Section root element.
 */
export function scheduleProgressUpdate( activeIndex, section ) {
	window.requestAnimationFrame( () => {
		window.requestAnimationFrame( () => {
			updateProgressBar( activeIndex, section );
		} );
	} );
}

/**
 * Measure each content panel height for adaptive layout.
 *
 * @param {HTMLElement} section Section root element.
 * @return {number[]} Panel heights in pixels.
 */
export function measureContentPanelHeights( section ) {
	const panels = section.querySelectorAll( '[data-journey-content-panel]' );
	const contentArea = section.querySelector( '[data-journey-content]' );
	const contentWidth = contentArea?.getBoundingClientRect().width || 0;
	const heights = [];

	panels.forEach( ( panel ) => {
		const previousPosition = panel.style.position;
		const previousVisibility = panel.style.visibility;
		const previousOpacity = panel.style.opacity;
		const previousWidth = panel.style.width;
		const wasHidden = panel.hidden;

		panel.hidden = false;
		panel.style.position = 'static';
		panel.style.visibility = 'hidden';
		panel.style.opacity = '0';
		panel.style.pointerEvents = 'none';

		if ( contentWidth > 0 ) {
			panel.style.width = `${ contentWidth }px`;
		}

		heights.push( Math.ceil( panel.offsetHeight ) );

		panel.hidden = wasHidden;
		panel.style.position = previousPosition;
		panel.style.visibility = previousVisibility;
		panel.style.opacity = previousOpacity;
		panel.style.pointerEvents = '';
		panel.style.width = previousWidth;
	} );

	return heights;
}

/**
 * Measure each original image slide height for adaptive layout.
 *
 * @param {HTMLElement} sliderRoot Splide root element.
 * @param {HTMLElement} mediaArea  Media column element.
 * @return {number[]} Image slide heights in pixels.
 */
export function measureImageSlideHeights( sliderRoot, mediaArea ) {
	const containerWidth =
		mediaArea?.getBoundingClientRect().width ||
		sliderRoot.getBoundingClientRect().width ||
		0;
	const slideElements = sliderRoot.querySelectorAll(
		'.splide__slide:not(.splide__slide--clone)'
	);
	const heights = [];

	slideElements.forEach( ( slideElement ) => {
		const image = slideElement.querySelector(
			'.alteia-our-journey-section__slide-image'
		);

		if ( ! image ) {
			heights.push( 0 );
			return;
		}

		let height = 0;

		if (
			image.naturalWidth > 0 &&
			image.naturalHeight > 0 &&
			containerWidth > 0
		) {
			height = Math.ceil(
				( image.naturalHeight / image.naturalWidth ) * containerWidth
			);
		} else {
			height = Math.ceil( image.getBoundingClientRect().height );
		}

		heights.push( Math.max( height, 0 ) );
	} );

	return heights;
}

/**
 * Apply adaptive heights for the active slide (content column + image track).
 *
 * @param {HTMLElement} section                    Section root element.
 * @param {HTMLElement} sliderRoot                 Splide root element.
 * @param {number}      activeIndex                Active slide index.
 * @param {number[]}    contentHeights             Measured content panel heights.
 * @param {number[]}    imageHeights               Measured image slide heights.
 * @param {Object}      [options]                  Layout options.
 * @param {boolean}     [options.setContentHeight] Whether to set content min-height.
 */
export function applyAdaptiveHeights(
	section,
	sliderRoot,
	activeIndex,
	contentHeights,
	imageHeights,
	options = {}
) {
	const setContentHeight = options.setContentHeight !== false;
	const maxIndex = Math.max( contentHeights.length, imageHeights.length ) - 1;

	if ( maxIndex < 0 ) {
		return;
	}

	const safeIndex = clampIndex( activeIndex, 0, maxIndex );
	const contentArea = section.querySelector( '[data-journey-content]' );
	const track = sliderRoot.querySelector( '.splide__track' );
	const contentHeight = contentHeights[ safeIndex ] || 0;
	const imageHeight = imageHeights[ safeIndex ] || 0;

	if ( setContentHeight && contentArea && contentHeight > 0 ) {
		contentArea.style.setProperty(
			'--alteia-journey-content-min-height',
			`${ contentHeight }px`
		);
	}

	if ( track && imageHeight > 0 ) {
		track.style.height = `${ imageHeight }px`;
	}
}

/**
 * Measure and apply adaptive heights for a slide index.
 *
 * @param {HTMLElement} section     Section root element.
 * @param {HTMLElement} sliderRoot  Splide root element.
 * @param {number}      activeIndex Active slide index.
 * @param {Object}      [options]   Passed to applyAdaptiveHeights.
 * @return {{ contentHeights: number[], imageHeights: number[] }} Measured heights.
 */
export function measureAndApplyHeights(
	section,
	sliderRoot,
	activeIndex,
	options = {}
) {
	const mediaArea = section.querySelector( '[data-journey-media]' );
	const contentHeights = measureContentPanelHeights( section );
	const imageHeights = measureImageSlideHeights( sliderRoot, mediaArea );

	applyAdaptiveHeights(
		section,
		sliderRoot,
		activeIndex,
		contentHeights,
		imageHeights,
		options
	);

	return { contentHeights, imageHeights };
}
